import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import { CrossIcon, ModalTop, ModalBottom } from "../../assets/Icon/ModalIcon";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  CloseButton,
  CloseButtonWrapper,
  ModalWrapper,
} from "../../components/style/modalStyle";
import { TrashIcon } from "../../assets/Icon/headerCicon";
import { useDispatch } from "react-redux";
import { AdminCreateInvoice } from "../../stores/orders";
import { setToast } from "../../stores/toast/toastslice";
import { getClientList } from "../../stores/clients";

const YEARLY_ITEMS = [
  { lable: "Invoices", id: "INVOICING" },
  { lable: "Bookkeeping", id: "BOOKKEEPING" },
];
const SUBSCRIPTIONS_ITEMS = [{ lable: "Bookkeeping", id: "BOOKKEEPING" }];
const ONE_TIME_ITEMS = [
  { lable: "Tax Filing", id: "TAX_FILING" },
  { lable: "LLC Incorporation", id: "EIN" },
  { lable: "ITIN", id: "ITIN" },
  { lable: "Operating Agreement", id: "OPERATING_AGREEMENT" },
];

const ALL_ITEMS = [...SUBSCRIPTIONS_ITEMS, ...YEARLY_ITEMS, ...ONE_TIME_ITEMS];

// Create: { "Invoices": 4, "Bookkeeping": 6, ... }
const ITEM_ID_MAP = ALL_ITEMS.reduce<Record<string, string>>((acc, item) => {
  acc[item.id] = item.lable;
  return acc;
}, {});

// Define a type for line items
export type LineItem = {
  item: string;
  description?: string;
  unit: string;
  quantity: string;
  amount: string;
};

const CreateInvoice = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [toolsDropdown, setToolsDropdown] = useState<any>();

  const [open, setOpen] = useState(false);

  const wrapperRef = useRef(null);

  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [clients, setClients] = useState<any>([]);
  const [lineItems, setLineItems] = React.useState<LineItem[]>([]);
  const [selectSubscriptionType, setSelectSubscriptionType] = useState("");
  const [submittingType, setSubmittingType] = useState("");
  const [saveClientError, setSaveClientError] = useState("");
  const [noteValue, setNoteValue] = useState("");
  const [searchValue, setSearchValue] = useState<any>("");
  const [addclient, setAddClient] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const [loadingClients, setLoadingClients] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 20;
  const navigate = useNavigate();
  const { id } = useParams();

  const viewOnly = id ? true : false;

  const isLineItemValid = (item: LineItem) => {
    return item.item && item.unit && item.quantity && item.amount;
  };

  const areAllLineItemsValid =
    lineItems?.length > 0 && lineItems.every(isLineItemValid);

  // check all required fields are filled
  const isFieldsFilled =
    selectSubscriptionType && areAllLineItemsValid && selectedItem;

  const fetchClientsList = async (pageNumber = 1, search = "") => {
    if (loadingClients || !hasMore) return;

    setLoadingClients(true);

    try {
      const offset = (pageNumber - 1) * limit;

      const res: any = await dispatch<any>(
        getClientList({
          offset,
          limit,
          search,
        }),
      );

      const newData = res?.payload?.data || [];

      // append
      setClients((prev: any) =>
        pageNumber === 1 ? newData : [...prev, ...newData],
      );

      // stop further calls if returned less than limit
      if (newData.length < limit) {
        setHasMore(false);
      }
    } catch (err) {
    } finally {
      setLoadingClients(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSelectedItem(null);

    // If cleared → immediate reset API
    if (value.trim() === "") {
      setPage(1);
      fetchClientsList(1, "");
      return;
    }

    // Otherwise new debounce search
    const timeout = setTimeout(() => {
      setPage(1);
      fetchClientsList(1, value);
    }, 400);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    fetchClientsList(1);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      //@ts-ignore
      if (!wrapperRef.current?.contains(e.target)) {
        setSearchValue("");
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------- SELECT ITEM ----------------
  const handleSelect = (item: any) => {
    setSelectedItem(item);
    setSearchValue(item.name);
    setOpen(false);
  };

  // on scroll pagination
  const handleListboxScroll = (e: any) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 10;

    if (bottom && !loadingClients) {
      const next = page + 1;
      setPage(next);
      fetchClientsList(next);
    }
  };

  const handleSubmit = async () => {
    try {
      if (isFieldsFilled) {
        const response = await dispatch<any>(
          AdminCreateInvoice({
            line_item: lineItems,
            clientDetails: selectedItem,
            clientId: selectedItem?.id,
            subscription_type: selectSubscriptionType,
            notes: noteValue,
          }),
        ).unwrap();
        if (response?.status >= 400) {
          dispatch<any>(
            setToast({
              message: response?.data?.message,
              type: "error",
              title: "Error",
            }),
          );
          setSubmittingType("");
          return;
        }
        if (response) {
          dispatch<any>(
            setToast({
              message: "Invoice Created!",
              type: "success",
              title: "Success",
            }),
          );
          navigate("/admin-invoices");
        }
      } else {
        dispatch<any>(
          setToast({
            message: "Please fill the required fields!",
            type: "error",
            title: "Error",
          }),
        );
      }
    } catch (error: any) {
      console.log(error);
      dispatch<any>(
        setToast({
          message: error?.payload?.data?.message,
          type: "error",
          title: "Error",
        }),
      );
    }
  };

  const handleSaveClient = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent page refresh
    if (addclient.first_name && addclient?.last_name && addclient.email) {
      // Create a new client object with unique id
      const newClient = {
        first_name: addclient.first_name,
        last_name: addclient.last_name,
        email: addclient.email,
      };

      // Add to clients array
      setClients((prev: any) => [newClient, ...prev]);

      // Automatically select the new client in Autocomplete
      setSelectedItem(newClient);

      // Reset add client form
      setAddClient({ first_name: "", last_name: "", email: "" });

      // Close modal
      setOpenModal(false);
    } else {
      setSaveClientError("All fields are required!");
    }
  };

  const handleAddClient = (e: any) => {
    const { name, value } = e.target;
    setAddClient((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSaveClientError("");
  };

  // Handler to add a new line item
  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        item: "",
        description: "",
        unit: "",
        quantity: "1",
        amount: "",
      },
    ]);
  };

  // Handler to update a field in a line item
  const handleLineItemChange = (
    index: number,
    field: keyof LineItem,
    value: string,
  ) => {
    // Prevent more than 6 digits for unit and quantity
    if (
      (field === "unit" || field === "quantity") &&
      value.replace(/\D/g, "").length > 6
    ) {
      return;
    }

    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;

    // Recalculate amount if unit or quantity changes
    if (field === "unit" || field === "quantity") {
      const rawUnit = updatedItems[index].unit || "0";
      const rawQty = updatedItems[index].quantity || "1";

      const unit = parseFloat(rawUnit) || 0;
      const quantity = parseFloat(rawQty) || 1; // default to 1 if empty or 0

      const amount = unit * quantity;
      updatedItems[index].amount = amount ? amount.toFixed(2) : "";
    }

    setLineItems(updatedItems);
  };

  // Handler to delete a line item
  const handleDeleteLineItem = (index: number) => {
    const removedItem = lineItems[index]?.item;

    setLineItems((prev) => prev.filter((_, i) => i !== index));

    const baseItems =
      selectSubscriptionType === "one_time"
        ? ONE_TIME_ITEMS
        : selectSubscriptionType === "yearly"
          ? YEARLY_ITEMS
          : SUBSCRIPTIONS_ITEMS;

    const itemToRestore = baseItems.find((item) => item.id === removedItem);

    if (itemToRestore) {
      setToolsDropdown((prev: any) => {
        if (prev.some((i: any) => i.id === itemToRestore.id)) return prev;
        return [itemToRestore, ...prev];
      });
    }
  };

  return (
    <div>
      <Button
        sx={{
          mb: 1,
          color: "#6C6F71",
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 600,
          gap: 0.5,
          ml: -2,
        }}
        onClick={() => navigate("/admin-invoices")}
      >
        <ArrowBackIosNewIcon style={{}} /> Back
      </Button>
      <div
        style={{
          color: "#060110",
          fontWeight: 500,
          fontSize: "24px",
          marginBottom: "20px",
        }}
      >
        Create Invoice
      </div>
      <div
        style={{
          display: isTablet ? "grid" : "flex",
          alignItems: "center",
          gap: 30,
        }}
      >
        <Box sx={{ width: isTablet ? "100%" : "500px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontWeight: 500, marginBottom: 1 }}>
              Client <span style={{ color: "red" }}>{"*"}</span>
            </Typography>
            {!viewOnly && (
              <Button
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  height: 4,
                }}
                onClick={() => setOpenModal(true)}
              >
                <AddCircleOutlineOutlinedIcon
                  style={{
                    width: "16px",
                  }}
                />
                Add New Client
              </Button>
            )}
          </div>

          <div
            ref={wrapperRef}
            style={{
              position: "relative",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            {/* INPUT */}
            <input
              value={
                searchValue ||
                (selectedItem &&
                  `${selectedItem?.first_name} ${selectedItem?.last_name}`)
              }
              onChange={handleChange}
              onClick={() => {
                setOpen(true);
                fetchClientsList(1, searchValue);
              }}
              placeholder="Select Client"
              style={{
                width: "100%",
                height: 46,
                border: "1px solid #ccc",
                borderRadius: 10,
                padding: "0 10px",
                fontSize: 16,
                fontWeight: 500,
                outline: "none",
                transition: "all .2s",
              }}
              onFocusCapture={(e) => (e.target.style.borderColor = "#2977CC")}
              onBlurCapture={(e) => (e.target.style.borderColor = "#ccc")}
            />

            {/* DROPDOWN */}
            {open && (
              <div
                onScroll={handleListboxScroll}
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  width: "100%",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  maxHeight: 300,
                  overflowY: "auto",
                  zIndex: 1000,
                }}
              >
                {clients?.length === 0 && (
                  <div style={{ padding: 12, color: "#9ca3af", fontSize: 13 }}>
                    No results
                  </div>
                )}

                {clients?.map((item: any, index: number) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      handleSelect(item);
                      setOpen(false);
                    }}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      fontSize: 16,
                      borderBottom: "1px solid #f1f1f1",
                    }}
                  >
                    {item.first_name} {item.last_name}
                  </div>
                ))}

                {loadingClients && page > 1 && (
                  <div
                    style={{
                      padding: 8,
                      textAlign: "center",
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    Loading more...
                  </div>
                )}
              </div>
            )}
          </div>
        </Box>

        <div>
          <Typography sx={{ fontWeight: 500, marginBottom: 1 }}>
            Subscription Type <span style={{ color: "red" }}>{"*"}</span>
          </Typography>
          <Select
            value={selectSubscriptionType || ""}
            onChange={(e) => {
              setSelectSubscriptionType(e.target.value);
              if (e.target.value === "one_time") {
                setToolsDropdown(ONE_TIME_ITEMS);
              } else if (e.target.value === "yearly") {
                setToolsDropdown(YEARLY_ITEMS);
              } else {
                setToolsDropdown(SUBSCRIPTIONS_ITEMS);
              }
              // Reset only the 'item' field of all line items
              setLineItems((prevLineItems) =>
                prevLineItems.map((lineItem) => ({
                  ...lineItem,
                  item: "", // Reset the 'item' field to empty
                })),
              );
            }}
            disabled={viewOnly}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span style={{ color: "#afafaf", fontWeight: 400 }}>
                    Subscription Type
                  </span>
                );
              }

              if (selected === "one_time") return "One Time";
              if (selected === "monthly") return "Monthly";
              if (selected === "yearly") return "Yearly";
            }}
            sx={{
              width: isTablet ? "100%" : "500px",
              height: "48px",
              borderRadius: "10px",
              background: "#fff",
              fontSize: "16px",

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ccc",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2977CC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2977CC",
              },

              "& .MuiSelect-select": {
                padding: "12px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiMenuItem-root": {
                borderRadius: "6px",
                margin: "4px 8px",
                "&:hover": {
                  backgroundColor: "#EAF1FA",
                },
                "&.Mui-selected": {
                  backgroundColor: "#EAF1FA",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#EAF1FA",
                  },
                },
              },
            }}
          >
            <MenuItem value="" disabled sx={{ display: "none" }}>
              Subscription Type
            </MenuItem>
            <MenuItem value="one_time">One Time</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
          </Select>
        </div>
      </div>

      {/**LIST ITEM */}
      <Box
        sx={{
          background: "white",
          borderRadius: "10px",
          boxShadow: "0px 2px 4px 0px #D1DEEB33",
          my: 3,
          mt: 4,
          paddingBottom: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: "20px",
            paddingLeft: "20px",
            paddingTop: "20px",
            paddingBottom: "0px",
          }}
        >
          <p
            style={{
              color: "#060110",
              fontSize: isMobile ? "20px" : "24px",
              fontWeight: 500,
            }}
          >
            Line Items <span style={{ color: "red" }}>{"*"}</span>
          </p>
          {!viewOnly && (
            <Button
              sx={{
                fontSize: "12px",
                px: 1.5,
                py: 1,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "white",
                background:
                  (selectSubscriptionType === "one_time" &&
                    lineItems?.length === 4) ||
                  (selectSubscriptionType === "monthly" &&
                    lineItems?.length === 1) ||
                  (selectSubscriptionType === "yearly" &&
                    lineItems?.length === 2)
                    ? "#ccc"
                    : "#2977CC",
                width: "131px",
                height: isMobile ? "38px" : "41px",
              }}
              onClick={handleAddLineItem}
              disabled={
                (selectSubscriptionType === "one_time" &&
                  lineItems?.length === 4) ||
                (selectSubscriptionType === "monthly" &&
                  lineItems?.length === 1) ||
                (selectSubscriptionType === "yearly" && lineItems?.length === 2)
              }
            >
              <AddCircleOutlineOutlinedIcon
                style={{
                  width: "16px",
                }}
              />
              Add new item
            </Button>
          )}
        </div>
        {/* Line Items Table */}
        <Box sx={{ mt: 3, mb: 3, width: "100%", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "600px",
            }}
          >
            <thead>
              <tr style={{ background: "#F4F7FBB2" }}>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#6C6F71",
                    paddingLeft: "25px",
                  }}
                >
                  PRODUCTS / SERVICES
                </th>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#6C6F71",
                  }}
                >
                  DESCRIPTION{" "}
                  <span
                    style={{
                      fontSize: "12px",
                    }}
                  >
                    (OPTIONAL)
                  </span>
                </th>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#6C6F71",
                  }}
                >
                  UNIT COST
                </th>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#6C6F71",
                  }}
                >
                  QUANTITY
                </th>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: "#6C6F71",
                  }}
                >
                  AMOUNT
                </th>
                <th
                  style={{
                    fontSize: "14px",
                    padding: "10px",
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#2C3034",
                  }}
                ></th>
              </tr>
            </thead>
            {lineItems.length > 0 && (
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "2px solid #EAF1FA99" }}>
                    <td style={{ padding: "20px" }}>
                      <Select
                        value={item.item || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const prevValue = lineItems[idx]?.item;

                          handleLineItemChange(idx, "item", newValue);

                          setToolsDropdown((prev: any[]) => {
                            let updated = [...prev];

                            // restore previous item
                            if (prevValue) {
                              const baseItems =
                                selectSubscriptionType === "one_time"
                                  ? ONE_TIME_ITEMS
                                  : selectSubscriptionType === "yearly"
                                    ? YEARLY_ITEMS
                                    : SUBSCRIPTIONS_ITEMS;

                              const prevItem = baseItems.find(
                                (i) => i.id === prevValue,
                              );

                              if (
                                prevItem &&
                                !updated.some((i) => i.id === prevItem.id)
                              ) {
                                updated.push(prevItem);
                              }
                            }

                            // remove new selection
                            updated = updated.filter((i) => i.id !== newValue);

                            return updated;
                          });
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <span style={{ color: "#979797" }}>
                                Select Product
                              </span>
                            );
                          }

                          return ITEM_ID_MAP[selected] || selected;
                        }}
                        displayEmpty
                        sx={{
                          width: "210px",
                          height: "36px",
                          borderRadius: "6px",
                          background: "#fff",
                          fontSize: "16px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#EAF1FA",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#2977CC",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#2977CC",
                          },

                          "& .MuiSelect-select": {
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiMenuItem-root": {
                            borderRadius: "6px",
                            margin: "4px 8px",
                            "&:hover": {
                              backgroundColor: "#EAF1FA",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#EAF1FA",
                              fontWeight: 500,
                              "&:hover": {
                                backgroundColor: "#EAF1FA",
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled style={{ display: "none" }}>
                          Select Product
                        </MenuItem>
                        {toolsDropdown?.map((item: any) => {
                          return (
                            <MenuItem value={item?.id}>{item?.lable}</MenuItem>
                          );
                        })}
                      </Select>
                    </td>
                    <td style={{ padding: "8px" }}>
                      <input
                        className="line-item-input"
                        maxLength={200}
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          handleLineItemChange(
                            idx,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder="Description"
                        style={{
                          width: "224px",
                          height: "36px",
                          fontSize: "16px",
                          borderRadius: 6,
                          padding: 8,
                          background: "#fff",
                        }}
                        // disabled={isViewOnly}
                      />
                      {item?.description?.length === 200 && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 14,
                            marginTop: 2,
                          }}
                        >
                          Cannot exceed 200 characters
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <input
                        className="line-item-input"
                        type="text"
                        maxLength={6}
                        value={item.unit}
                        onChange={(e) => {
                          // Allow only numbers and a single decimal point, but not as the first character
                          let val = e.target.value.replace(/[^0-9.]/g, "");
                          // Prevent decimal as first character
                          if (val.startsWith(".")) {
                            val = "";
                          }
                          // Only one decimal allowed
                          val = val.replace(/(\..*)\./g, "$1");
                          // Limit to 2 decimal places
                          if (val.includes(".")) {
                            const parts = val.split(".");
                            if (parts[1] && parts[1].length > 2) {
                              val = parts[0] + "." + parts[1].substring(0, 2);
                            }
                          }
                          handleLineItemChange(idx, "unit", val);
                        }}
                        placeholder="Cost"
                        style={{
                          width: "77px",
                          height: "36px",
                          fontSize: "16px",
                          borderRadius: 6,
                          padding: 8,
                          background: "#fff",
                        }}
                        // disabled={isViewOnly}
                      />
                      {item.unit && item.unit.replace(/\D/g, "").length > 6 && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          Unit cost cannot exceed 6 digits.
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <input
                        className="line-item-input"
                        type="text"
                        value={item.quantity}
                        placeholder="Qty."
                        style={{
                          width: "52px",
                          height: "36px",
                          fontSize: "16px",
                          borderRadius: 6,
                          padding: 8,
                          background: "#fff",
                        }}
                        disabled
                      />
                      {item.quantity &&
                        item.quantity.replace(/\D/g, "").length > 6 && (
                          <div
                            style={{
                              color: "red",
                              fontSize: 12,
                              marginTop: 2,
                            }}
                          >
                            Quantity cannot exceed 6 digits.
                          </div>
                        )}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <div
                        className="line-item-input"
                        style={{
                          minWidth: "120px",
                          width: "max-content",
                          maxWidth: "100%",
                          height: "36px",
                          fontSize: "16px",
                          borderRadius: 6,
                          padding: 4,
                          background: "#fff",
                          color:
                            item?.amount && parseFloat(item.amount) > 0
                              ? "#01060B"
                              : "#97999B",
                          textAlign: "left",
                          overflowX: "auto",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {"$"}
                        {item?.amount
                          ? parseFloat(item.amount).toFixed(2)
                          : "0.00"}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      {!viewOnly && (
                        <Button
                          onClick={() => handleDeleteLineItem(idx)}
                          sx={{
                            width: "20px",
                          }}
                        >
                          <TrashIcon />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {lineItems.length === 0 && (
            <div
              style={{
                background: "#ffffff",
                color: "#6C6F71",
                fontSize: "14px",
                padding: 20,
                marginTop: 3,
                marginBottom: 0,
                textAlign: "center",
                borderBottom: "1px solid #EAF1FA99",
              }}
            >
              No Line item added!
            </div>
          )}
        </Box>
      </Box>

      <Box>
        <p
          style={{
            color: "#2C3034",
            fontWeight: 500,
            fontSize: "16px",
            marginBottom: 10,
          }}
        >
          Notes{" "}
          <span
            style={{
              fontSize: "14px",
              color: "#8b8b8b",
              marginLeft: "2px",
            }}
          >
            (Optional)
          </span>
        </p>
        <textarea
          name="notes"
          value={noteValue}
          maxLength={500}
          onChange={(e) => setNoteValue(e.target.value)}
          placeholder="Enter notes here..."
          style={{
            border: "2px solid #EAF1FA",
            background: "white",
            fontSize: "16px",
            borderRadius: "10px",
            width: isMobile ? "100%" : "482px",
            height: "141px",
            padding: 20,
            resize: "none",
            fontFamily: "Plus Jakarta Sans",
            outline: "none", // removes blue default outline
          }}
          onFocus={(e) => {
            e.target.style.border = "2px solid #2977CC"; // focus color
          }}
          onBlur={(e) => {
            e.target.style.border = "2px solid #EAF1FA"; // back to normal
          }}
          disabled={viewOnly}
        />
      </Box>

      {!viewOnly && (
        <Button
          sx={{
            width: isMobile ? "100%" : "191px",
            height: "52px",
            fontSize: "16px",
            fontWeight: 600,
            borderRadius: "8px",
            mt: 2,
            background:
              "linear-gradient(98.24deg, #2977CC 4.13%, #1D538F 128.24%)",
            color: "white !important",
            position: "relative",
            pointerEvents: "auto",
            opacity: 1,
          }}
          onClick={() => {
            if (isFieldsFilled) {
              setSubmittingType("save");
            }
            handleSubmit();
          }}
        >
          {submittingType === "save" ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Create"
          )}
        </Button>
      )}

      {/* Modal for Add New Client */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <ModalWrapper
          sx={{
            width: isMobile ? "350px" : "550px",
            p: 0,
            height: "425px",
            backgroundImage: "none",
            position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
          }}
        >
          {/* Top right decoration */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <ModalTop />
          </div>
          {/* Bottom left decoration */}
          <div
            style={{
              position: "absolute",
              bottom: -6,
              left: 0,
              zIndex: 1,
            }}
          >
            <ModalBottom />
          </div>
          {/* Ensure close button is above decorations */}
          <CloseButtonWrapper
            sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
          >
            <CloseButton
              onClick={() => {
                setOpenModal(false);
                setAddClient({ first_name: "", last_name: "", email: "" });
                setSaveClientError("");
              }}
            >
              <CrossIcon />
            </CloseButton>
          </CloseButtonWrapper>
          <Box sx={{ p: 4, pt: 0 }}>
            <div
              style={{
                fontSize: "24px",
                color: "#01060B",
                fontWeight: 700,
                marginTop: 5,
                marginBottom: 20,
              }}
            >
              Add New Client
            </div>
            <form onSubmit={handleSaveClient}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                  }}
                >
                  <label
                    style={{
                      fontWeight: 500,
                      color: "#2C3034",
                      fontSize: "16px",
                    }}
                  >
                    Email Address
                  </label>
                  <input
                    style={{
                      padding: "16px",
                      borderRadius: "8px",
                      border: "0.5px solid #A7C7EA",
                      fontSize: "16px",
                      outline: "none",
                      transition: "border 0.2s",
                      boxShadow: "0 1px 8px rgba(0, 0, 0, 0.1)",
                    }}
                    placeholder="Enter email address"
                    name="email"
                    value={addclient.email}
                    onChange={handleAddClient}
                    onFocus={(e) =>
                      (e.target.style.border = "2px solid #2977CC")
                    }
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 7,
                      marginTop: "15px",
                    }}
                  >
                    <label
                      style={{
                        fontWeight: 500,
                        color: "#2C3034",
                        fontSize: "16px",
                      }}
                    >
                      First Name
                    </label>
                    <input
                      style={{
                        padding: "16px",
                        borderRadius: "8px",
                        border: "0.5px solid #A7C7EA",
                        fontSize: "16px",
                        outline: "none",
                        transition: "border 0.2s",
                        boxShadow: "0 1px 8px rgba(0, 0, 0, 0.1)",
                      }}
                      name="first_name"
                      placeholder="Enter first name"
                      maxLength={60}
                      value={addclient.first_name}
                      onChange={handleAddClient}
                      onFocus={(e) =>
                        (e.target.style.border = "2px solid #2977CC")
                      }
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 7,
                      marginTop: "15px",
                    }}
                  >
                    <label
                      style={{
                        fontWeight: 500,
                        color: "#2C3034",
                        fontSize: "16px",
                      }}
                    >
                      Last Name
                    </label>
                    <input
                      style={{
                        padding: "16px",
                        borderRadius: "8px",
                        border: "0.5px solid #A7C7EA",
                        fontSize: "16px",
                        outline: "none",
                        transition: "border 0.2s",
                        boxShadow: "0 1px 8px rgba(0, 0, 0, 0.1)",
                      }}
                      name="last_name"
                      placeholder="Enter last name"
                      maxLength={60}
                      value={addclient.last_name}
                      onChange={handleAddClient}
                      onFocus={(e) =>
                        (e.target.style.border = "2px solid #2977CC")
                      }
                    />
                  </div>
                </div>
                {saveClientError && (
                  <div
                    style={{
                      color: "red",
                      margin: "auto",
                      marginTop: "10px",
                    }}
                  >
                    {saveClientError}
                  </div>
                )}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mt: saveClientError ? 2 : 4,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    sx={{
                      border: "2px solid #2977CC",
                      borderRadius: "8px",
                      width: "192px",
                      height: "51px",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#2977CC",
                    }}
                    onClick={() => {
                      setOpenModal(false);
                      setAddClient({
                        first_name: "",
                        last_name: "",
                        email: "",
                      });
                      setSaveClientError("");
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    sx={{
                      background: "linear-gradient(to right, #2977CC, #1D538F)",
                      color: "white",
                      width: "192px",
                      height: "51px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                    type="submit"
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </ModalWrapper>
      </Modal>
    </div>
  );
};

export default CreateInvoice;
